import TextField, { TextFieldProps } from "@mui/material/TextField";
import { Controller } from "react-hook-form";
import { new_ErrorAlert } from "../../uni-questionnaire/export_spwk/ExportManager";
import { RhfProps } from "./types/RhfProps";
import { RhfStyleProps } from "./types/RhfStyleProps";
import { useTranslation } from "react-i18next";

// TODO 修正數字問題
// https://stackoverflow.com/questions/50823182/material-ui-remove-up-down-arrow-dials-from-textview
// https://stackoverflow.com/questions/58712520/force-textfield-with-type-number-to-display-dot-as-decimal-separator

type Props = RhfProps &
  RhfStyleProps &
  TextFieldProps & {
    digit?: number; // 小數位數 0=整數、undefined=任意小數
    min?: number;
    max?: number;
    displayMember?: string;
    valueMember?: string;
    emptyOption?: string;

    use_half_mode: boolean;

    showErrorText?: boolean;
  };

const RhfNumberPicker = ({
  rhf,
  name,
  defaultValue,
  rules, // RhfProps
  sx,
  className,
  style, // RhfStyleProps

  digit,
  min,
  max,
  displayMember,
  valueMember,
  emptyOption,

  use_half_mode = true, // 剛開始特作小數點已經是以 .5 為單位調整， 所以預設為 true

  showErrorText,

  ...restProps // TextFieldProps
}: Props) => {
  const { t } = useTranslation();

  if (displayMember == null) {
    displayMember = "label";
  }

  if (valueMember == null) {
    valueMember = "value";
  }

  if (showErrorText == null) {
    showErrorText = true; // 預設顯示錯誤訊息
  }

  /** 是否使用 0.5 為單位 */
  if (restProps.InputProps == null) {
    let step = 1;

    if (digit != null && digit !== 0) {
      if (use_half_mode) {
        step = (1.0 / Math.pow(10, digit)) * 5; // 0.5 為單位 (注意! 0.05 不支援，只能小數點下1位)
      } else {
        step = (1.0 / Math.pow(10, digit)) * 1; // 0.1 為單位
      }
    }

    restProps.InputProps = {
      inputProps: { min: min, max: max, step: step, inputMode: "decimal" },
    };
  } else {
    throw new_ErrorAlert("TODO 1464684 仔細設定 min max 到傳入的參數");
  }

  return (
    <Controller
      control={rhf?.control} // 必要綁定，否則必須使用 useFormContext()
      name={name} // 必填，用來綁定 react-hook-form 對應的 value
      defaultValue={defaultValue == null ? "" : String(defaultValue)} // 如果沒指定，需要設定一個合理的初始數值，才會正常運作
      rules={rules} // react-hook-form 原生的驗證數值規則
      render={({ field, fieldState }) => {
        // 等同於: render={({ field: { onChange, onBlur, value, ref } }) => (
        const has_error = !!fieldState.error;
        return (
          <TextField
            type="number" // 數字型態
            {...restProps} // MUI 控制項的屬性
            sx={sx}
            className={className}
            style={style}
            // onChange={field.onChange}
            onChange={(e) => {
              // ref: https://stackoverflow.com/questions/47798104/set-min-max-on-textfield-type-number
              // 暫時解: 某些時候可能會輸入困難 40~120 輸入1 會變成40

              const ui_value = e.target.value;

              let num = undefined;
              if (digit === 0) {
                // 整數: UI上的數值取整數
                num = parseInt(ui_value, 10);
              } else {
                num = parseFloat(ui_value); // 小數: UI上的數值取小數

                if (digit != null && digit !== 0) {
                  // 如果需要限制小數點，則在此限制
                  // let s = num.toFixed(digit);// OK 但是輸入超過的小樹會亂跳
                  num = decimal_places(num, digit, use_half_mode);
                }
              }

              if (min != null) {
                // 超過最小值，則設為最小值
                if (num < min) {
                  num = min;
                }
              }

              if (max != null) {
                // 超過最大值，則設為最大值
                if (num > max) {
                  num = max;
                }
              }

              let str_val = "";
              if (isNaN(num) || ui_value === "") {
                // 解析失敗或空白
                str_val = "";
              } else {
                str_val = "" + num; // 設定改完之後的數值
              }

              field.onChange(str_val);
            }}
            onBlur={field.onBlur}
            value={field.value}
            ref={field.ref}
            // {...field} // 等同於: onChange={onChange} onBlur={onBlur} value={value} ref={ref}

            // 數值驗證錯誤提示
            error={showErrorText && has_error} // 紅色邊框
            helperText={
              showErrorText && has_error
                ? t(fieldState.error?.message ?? "")
                : undefined
            } // 錯誤訊息
          ></TextField>
        );
      }}
    />
  );
};

function decimal_places(num: number, digit: number, use_half_mode: boolean) {
  if (isNaN(num)) {
    return num;
  }

  let base = Math.pow(10, digit);

  num = Math.floor(num * base) / base;

  if (use_half_mode) {
    // 判斷是否為小數，如果是小數且小數點後一位大於等於 5，則將小數點後一位改為 5
    const decimalPart = num % 1;
    if (decimalPart > 0 && decimalPart < 0.5) {
      num += 0.5 - decimalPart;
    } else if (decimalPart > 0.5) {
      num += 1 - decimalPart;
    }
  }

  return num;
}

export default RhfNumberPicker;
